import { useCallback } from "react";
import {
  useAssignInterpreterToQuestionMutation,
  useCreateAssignmentsMutation,
  useLazyGetVideoResponsesQuery,
  useUpdateAssignmentStatusMutation,
} from "../store/api/apiSlice";
import {
  buildBulkAssignmentEntry,
  buildBulkAssignmentEntryFromAssignment,
  findMatchingVideoResponse,
  getInterpreterAssignmentPlan,
  mergeAssignmentWithVideoResponse,
  resolveInterpreterId,
} from "../utils/interpreterAssignment";

export const useInterpreterAssignment = () => {
  const [assignInterpreter, assignInterpreterState] =
    useAssignInterpreterToQuestionMutation();
  const [createAssignments, createAssignmentsState] =
    useCreateAssignmentsMutation();
  const [updateAssignment, updateAssignmentState] =
    useUpdateAssignmentStatusMutation();
  const [fetchVideoResponses] = useLazyGetVideoResponsesQuery();

  const isAssigning =
    assignInterpreterState.isLoading ||
    createAssignmentsState.isLoading ||
    updateAssignmentState.isLoading;

  const assignInterpreterToRecord = useCallback(
    async ({
      assignment,
      interpreterId: rawInterpreterId,
      interpreter,
      isSecondCheck = false,
      videoRefs,
    }) => {
      const interpreterId = rawInterpreterId ?? resolveInterpreterId(interpreter);

      if (!interpreterId) {
        throw new Error("Interpreter not found");
      }

      let assignmentRecord = assignment;
      const shouldLookupVideo = !isSecondCheck && Boolean(assignmentRecord?._id);

      if (shouldLookupVideo) {
        try {
          const videos = await fetchVideoResponses({}).unwrap();
          const matchedVideo = findMatchingVideoResponse(
            videos,
            assignmentRecord,
          );

          if (matchedVideo) {
            assignmentRecord = mergeAssignmentWithVideoResponse(
              assignmentRecord,
              matchedVideo,
            );
          }
        } catch {
          // Fall through and let the plan / payload builder report missing fields.
        }
      }

      const plan = getInterpreterAssignmentPlan({
        assignment: assignmentRecord,
        interpreterId,
        isSecondCheck,
      });

      if (plan.type === "invalid") {
        throw new Error(plan.message);
      }

      if (plan.type === "update") {
        await updateAssignment(plan.payload).unwrap();
        
        return {
          method: "update",
          isSecondCheck: plan.payload.isSecondChecked,
          assignmentId: plan.payload.assignmentId,
          interpreterId,
        };
      }

      if (plan.type === "create") {
        const entry = assignmentRecord
          ? await buildBulkAssignmentEntryFromAssignment({
              assignment: assignmentRecord,
              interpreterId,
              videoRefs: videoRefs || { current: {} },
            })
          : await buildBulkAssignmentEntry({
              response: plan.responseRecord,
              interpreterId,
              videoRefs: videoRefs || { current: {} },
            });

        await createAssignments({ assignment: [entry] }).unwrap();

        return {
          method: "create",
          assignmentId: assignmentRecord?._id,
          interpreterId,
        };
      }

      await assignInterpreter({
        questionId: plan.questionId,
        interpreterId: plan.interpreterId,
        organizationId: plan.organizationId,
      }).unwrap();

      return {
        method: "assignToQuestion",
        assignmentId: plan.assignmentId || assignment?._id,
        interpreterId,
      };
    },
    [assignInterpreter, createAssignments, fetchVideoResponses, updateAssignment],
  );

  const assignBulkResponses = useCallback(
    async ({
      responses,
      interpreterId: rawInterpreterId,
      interpreter,
      videoRefs,
    }) => {
      const interpreterId =
        rawInterpreterId ?? resolveInterpreterId(interpreter);

      if (!interpreterId) {
        throw new Error("Interpreter not found. Use the interpreter _id from GET /interpreter/all.");
      }

      if (!responses?.length) {
        throw new Error("No videos selected for assignment");
      }

      const assignments = await Promise.all(
        responses.map((response) =>
          buildBulkAssignmentEntry({
            response,
            interpreterId,
            videoRefs: videoRefs || { current: {} },
          }),
        ),
      );

      await createAssignments({ assignment: assignments }).unwrap();

      return { method: "create", count: responses.length, interpreterId };
    },
    [createAssignments],
  );

  return {
    assignInterpreterToRecord,
    assignBulkResponses,
    isAssigning,
  };
};
